import { LocationOutlined } from '@teko/ds-icon';
import {
  BottomNavigationBar,
  Button,
  Caption,
  Flex,
  Form,
  FormInstance,
  Input,
  Loading,
  Shape,
  styled,
} from '@teko/ds-mobile';
import { Select } from '@teko/ecommerce/mobile';
import { Area, SuggestAddress } from '@teko/location';
import { logger } from '@teko/log';
import { AddressFormType } from '@teko/ui-user';
import { AddressForm as BaseAddressForm } from '@teko/ui-user/mobile';
import { Address } from '@teko/user';
import { PATTERN_VALIDATION } from '@teko/utils';
import { useServices, useTenantConfig } from 'contexts';
import { useAddressFormV2, useSuggestionAddress } from 'hooks';
import { t } from 'i18n';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
interface AddressFormProps {
  editingAddress?: string;
  onClose: (addr: Address) => Promise<void>;
  allowAddDefaultAdress?: boolean;
}
const COUNT_STRING_WITHOUT_STREET_ADDRESS = 4;

export const AddressForm = ({
  editingAddress,
  onClose,
  allowAddDefaultAdress,
}: {
  editingAddress?: string;
  onClose: (addr: Address) => Promise<void>;
  allowAddDefaultAdress?: boolean;
}) => {
  const {
    locationsLimit,
    deliveryRequireEmail,
    epicOmni1589,
    epicOmni1825,
    epicOmni1825SuggestAddress,
  } = useTenantConfig();
  const { userService, locationService } = useServices();

  const { data: address = {} as Address, isValidating } = useSWR(
    !epicOmni1589 && !!editingAddress ? [`/addresses/${editingAddress}`] : null,
    async () => {
      const response = await userService.getAddress(editingAddress || '');
      return response;
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const {
    dataSuggestion,
    setSuggestInputText,
    openListSuggestion,
    setOpenListSuggestion,
    isLoadingSuggest,
    setProvince: setProvinceSuggest,
    setDistrict: setDistrictSuggest,
    setWard: setWardSuggest,
    getPlaceDetails,
  } = useSuggestionAddress({
    province: address?.provinceName || '',
    district: address?.districtName || '',
    ward: address?.wardName || '',
  });

  return epicOmni1589 ? (
    <AddressFormWithDataV2
      editingAddress={editingAddress}
      onClose={onClose}
      allowAddDefaultAdress={allowAddDefaultAdress}
    />
  ) : (
    <BaseAddressForm
      requireEmail={deliveryRequireEmail}
      locationsLimit={locationsLimit}
      editingAddress={editingAddress}
      locationService={locationService}
      userService={userService}
      onClose={onClose}
      allowAddDefaultAdress={allowAddDefaultAdress}
      address={address}
      loading={isValidating}
      dataSuggestion={dataSuggestion}
      openListSuggestion={openListSuggestion}
      onChangeAddress={(text) => {
        if (epicOmni1825SuggestAddress) {
          setSuggestInputText(text);
          setOpenListSuggestion(true);
        }
      }}
      isLoadingSuggest={isLoadingSuggest}
      setOpenListSuggestion={setOpenListSuggestion}
      customOnProvinceChanged={(province) => {
        setProvinceSuggest(province?.name || '');
        setDistrictSuggest('');
        setWardSuggest('');
      }}
      customOnDistrictChanged={(district) => {
        setDistrictSuggest(district?.name || '');
        setWardSuggest('');
      }}
      customOnWardChanged={(ward) => {
        setWardSuggest(ward?.name || '');
      }}
      isDisableFullAddressWhenNotOtherInfo={epicOmni1825SuggestAddress}
      getPlaceDetails={getPlaceDetails}
      showAddressNote={epicOmni1825}
    />
  );
};

const AddressFormWithDataV2 = (props: AddressFormProps) => {
  const { editingAddress, onClose, allowAddDefaultAdress } = props;
  const [form] = Form.useForm<AddressFormType>();
  const {
    locationsLimit,
    deliveryRequireEmail,
    epicOmni1589,
    epicOmni1825SuggestAddress,
  } = useTenantConfig();
  const { userService } = useServices();

  const { data: address = {} as Address, isValidating } = useSWR(
    epicOmni1589 && !!editingAddress ? [`/addresses/${editingAddress}`] : null,
    async () => {
      const response = await userService.getAddress(editingAddress || '');
      return response;
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const {
    message,
    submitForm,
    province,
    ward,
    provinces,
    wards,
    setWard,
    setProvince,
    setPlace,
    setLocation,
  } = useAddressFormV2({
    editingAddress: address,
    requireEmail: deliveryRequireEmail,
    allowAddDefaultAddress: allowAddDefaultAdress,
    locationsLimit: locationsLimit,
  });

  useEffect(() => {
    form.setFieldsValue({
      name: address.name || '',
      telephone: address.telephone,
      email: address.email ?? undefined,
      provinceCode: address.provinceCode,
      wardCode: address.wardCode,
      address: address.address,
      addressNote: address.addressNote,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address.name,
    address.telephone,
    address.email,
    address.provinceCode,
    address.wardCode,
    address.address,
    address.addressNote,
  ]);

  const {
    dataSuggestion,
    setSuggestInputText,
    openListSuggestion,
    setOpenListSuggestion,
    isLoadingSuggest,
    getPlaceDetails,
  } = useSuggestionAddress({
    province: province?.title || address?.provinceName || '',
    district: address?.districtName || '',
    ward: ward?.title || address?.wardName || '',
  });

  const onSubmitForm = async () => {
    try {
      await form.validateFields();
      const res = await submitForm(form.getFieldsValue());
      onClose(res);
    } catch (error) {
      logger.error('Error occurs when submit form address', error);
    }
  };

  const onClickSuggest = async (placeId: string) => {
    const items = await getPlaceDetails(placeId);
    if (!items || !Array.isArray(items) || !items.length) {
      return;
    }
    // items là danh sách các placeDetail và cần lấy placeDetail đầu tiên
    // trong case này chỉ gửi 1 placeID lên nên chỉ có 1 placeDetail
    const item = items?.[0];
    const address =
      item.fullAddress
        .split(',')
        ?.splice(
          0,
          item.fullAddress.split(',').length -
            COUNT_STRING_WITHOUT_STREET_ADDRESS,
        )
        ?.join(',') ||
      item.fullAddress ||
      '';
    setOpenListSuggestion(false);
    setPlace({ lat: item.lat, long: item.long });
    const newValue = {
      provinceCode: item.provinceCode || undefined,
      ...(item.wardCode ? { wardCode: item.wardCode } : {}),
    };
    form.setFieldsValue({
      address,
      ...newValue,
    });
    if (
      !!item.provinceId &&
      !!item.wardId &&
      !Number.isNaN(Number(item.provinceId)) &&
      !Number.isNaN(Number(item.wardId))
    ) {
      setLocation({
        provinceId: item.provinceCode,
        wardId: item.wardCode,
      });
    }
  };

  const resetPosition = () => {
    setPlace({
      lat: undefined,
      long: undefined,
    });
  };

  return address ? (
    <AddressFormWithV2
      loading={isValidating}
      form={form}
      message={message}
      requireEmail={deliveryRequireEmail}
      province={province}
      ward={ward}
      provinces={provinces}
      wards={wards}
      onChangeProvince={(province) => {
        setProvince(province);
        setWard(undefined);
        resetPosition();
      }}
      onChangeWard={(ward) => {
        setWard(ward);
        resetPosition();
      }}
      submitForm={onSubmitForm}
      dataSuggestion={dataSuggestion}
      openListSuggestion={openListSuggestion}
      onChangeAddress={(text) => {
        if (epicOmni1825SuggestAddress) {
          setSuggestInputText(text);
          setOpenListSuggestion(true);
          resetPosition();
        }
      }}
      onClickSuggest={onClickSuggest}
      isLoadingSuggest={isLoadingSuggest}
    />
  ) : null;
};

interface AddressFormWithV2Props {
  loading?: boolean;
  form: FormInstance<AddressFormType>;
  message: {
    success: boolean;
    content: string;
  };
  requireEmail?: boolean;
  provinces: Area[];
  wards: Area[];
  province?: Area;
  ward?: Area;
  onChangeProvince: (province: Area) => void;
  onChangeWard: (province: Area) => void;
  submitForm: () => Promise<void>;
  disableSubmit?: boolean;
  dataSuggestion?: SuggestAddress[];
  openListSuggestion?: boolean;
  onClickSuggest?: (placeId: string) => void;
  onChangeAddress?: (text: string) => void;
  isLoadingSuggest?: boolean;
}

const AddressFormWithV2 = (props: AddressFormWithV2Props) => {
  const {
    loading,
    message,
    form,
    requireEmail,
    province,
    ward,
    provinces,
    wards,
    onChangeProvince,
    onChangeWard,
    submitForm,
    disableSubmit,
    dataSuggestion,
    openListSuggestion,
    onClickSuggest,
    onChangeAddress,
    isLoadingSuggest,
  } = props;
  const { epicOmni1825 } = useTenantConfig();
  const [missingRequiredField, setMissingRequiredField] = useState({
    province: '',
    ward: '',
  });

  return (
    <Loading isLoading={loading}>
      {message.content && (
        <Caption color={message.success ? 'success500' : 'error500'}>
          {message.content}
        </Caption>
      )}
      <StyledForm layout="vertical" form={form}>
        <Shape
          backgroundColor="white"
          paddingLeft="s16"
          paddingRight="s16"
          paddingTop="s4"
          paddingBottom="s12"
        >
          <Form.Item
            label={t('Họ tên người nhận')}
            name="name"
            rules={[
              {
                required: true,
                message: t('${label} không được bỏ trống'),
              },
              {
                pattern: /[^!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]+$/g,
                message: t('${label} không đúng định dạng'),
              },
            ]}
          >
            <Input
              type="text"
              placeholder={t('Vui lòng nhập tên người nhận')}
            />
          </Form.Item>
          <Form.Item
            label={t('Số điện thoại người nhận')}
            name="telephone"
            rules={[
              {
                required: true,
                message: t('${label} không được bỏ trống'),
              },
              {
                pattern: /((\+84|84|0)+(3|5|7|8|9|1[2|6|8|9]))+([0-9]{8})\b/g,
                message: t('${label} không đúng định dạng'),
              },
            ]}
          >
            <Input
              type="text"
              placeholder={t('Nhập số điện thoại người nhận')}
            />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                required: requireEmail,
                message: t('${label} không được bỏ trống'),
              },
              {
                pattern: PATTERN_VALIDATION.email,
                message: t('${label} không đúng định dạng'),
              },
            ]}
          >
            <Input type="text" placeholder={t('Nhập email của bạn')} />
          </Form.Item>
          <StyledSelect
            title={t('Tỉnh/Thành phố')}
            placeholder={t('Tỉnh/Thành phố')}
            haveFilter
            popupTitle={t('Tỉnh/Thành phố')}
            items={provinces.map((p) => ({
              ...p,
              value: p.code,
            }))}
            required
            value={province?.title || ''}
            selectKey="title"
            onSelectChange={(value) => {
              const location = provinces.find(
                (prov) => prov.code === value.code,
              );
              if (location) {
                onChangeProvince(location);
                setMissingRequiredField({
                  ...missingRequiredField,
                  province: '',
                });
              }
            }}
            error={missingRequiredField.province}
          />
          <StyledSelect
            title={t('Phường/Xã')}
            placeholder={t('Phường/Xã')}
            haveFilter
            popupTitle={t('Phường/Xã')}
            items={wards.map((w) => ({
              ...w,
              value: w.code,
            }))}
            required
            value={ward?.title || ''}
            selectKey="title"
            onSelectChange={(value) => {
              const location = wards.find((w) => w.code === value.code);
              if (location) {
                onChangeWard(location);
                setMissingRequiredField({
                  ...missingRequiredField,
                  ward: '',
                });
              }
            }}
            error={missingRequiredField.province}
          />
          <Form.Item
            label={t('Địa chỉ cụ thể')}
            name="address"
            rules={[
              {
                required: true,
                message: t('${label} không được bỏ trống'),
              },
            ]}
          >
            <Input
              type="text"
              placeholder={t('Số nhà, ngõ, tên đường...')}
              onChange={(text) => {
                onChangeAddress?.(text);
              }}
            />
          </Form.Item>
          {epicOmni1825 && (
            <Form.Item
              label={t('Ghi chú')}
              name="addressNote"
              required={false}
              rules={[
                {
                  max: 500,
                  message: t('{{label}} không được vượt quá {{max}} ký tự', {
                    label: t('Ghi chú'),
                    max: 500,
                  }),
                },
              ]}
            >
              <Input
                placeholder={t('Ví dụ: Cổng màu xanh, nhà bên cạnh cây xăng')}
              />
            </Form.Item>
          )}
          {!!dataSuggestion &&
            !!openListSuggestion &&
            (!!dataSuggestion.length || isLoadingSuggest) && (
              <SuggestWrapper>
                {isLoadingSuggest ? (
                  <Flex justify="center" align="center">
                    <Spinner />
                  </Flex>
                ) : (
                  <>
                    <Text style={{ textAlign: 'left' }}>{t('Gợi ý')}</Text>
                    {dataSuggestion.map((item) => {
                      return (
                        <ItemSuggestion
                          key={item.fullAddress}
                          onClick={() => onClickSuggest?.(item.placeId)}
                        >
                          <LocationOutlined />
                          <Text style={{ textAlign: 'left', marginLeft: 8 }}>
                            {item.fullAddress}
                          </Text>
                        </ItemSuggestion>
                      );
                    })}
                  </>
                )}
              </SuggestWrapper>
            )}
        </Shape>
        <StyledBottomNavigationBar zIndex={1030}>
          <Button
            disabled={disableSubmit}
            variant="primary"
            onClick={submitForm}
            width={'100%'}
          >
            {t('Hoàn tất')}
          </Button>
        </StyledBottomNavigationBar>
      </StyledForm>
    </Loading>
  );
};

const StyledBottomNavigationBar = styled(BottomNavigationBar)`
  flex: 1;
  padding-right: 16px;
  padding-left: 16px;
  align-items: center;
  position: fixed;
`;

const StyledForm = styled(Form)`
  .teko-form-item {
    margin-bottom: 0;
  }
`;

const StyledSelect = styled(Select)`
  margin-top: 16px;
  input {
    border-radius: 4px;
  }
`;

export const SuggestWrapper = styled.div`
  background-color: #fafafa;
  border-radius: 12px;
  padding: 8px;
  margin-bottom: 15px;
  max-height: 200px;
  overflow-y: scroll;
`;

export const ItemSuggestion = styled.div`
  padding: 8px 10px;
  cursor: pointer;
`;

const Text = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #434657;
`;

export const Spinner = styled.div(
  () => `
  border: 3px solid #BBDEFB;
  border-top-color: #2196F3;
  border-radius: 50%;
  width: 1.5em;
  height: 1.5em;
  margin: 0 0.5rem;
  animation: spin 1s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`,
);
